<template>
  <Teleport to="body">
    <Transition name="google-review">
      <div v-if="review.visible" class="google-review" @keydown.esc="dismiss">
        <div class="google-review__backdrop" aria-hidden="true" @click="dismiss"></div>
        <section
          ref="sheet"
          tabindex="-1"
          class="google-review__sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="google-review-title"
          aria-describedby="google-review-text"
        >
          <button type="button" class="google-review__close" aria-label="Cerrar" @click="dismiss">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
          <div class="google-review__stars" aria-hidden="true">
            <i v-for="star in 5" :key="star" class="fa-solid fa-star" :style="{ animationDelay: `${star * 70}ms` }"></i>
          </div>
          <h2 id="google-review-title" class="google-review__title">{{ copy.title }}</h2>
          <p id="google-review-text" class="google-review__text">{{ copy.text }}</p>
          <a
            class="google-review__primary"
            :href="review.url"
            target="_blank"
            rel="noopener noreferrer"
            @click="markGoogleReviewClicked(review.url)"
          >
            <i class="fa-brands fa-google" aria-hidden="true"></i>
            Valorar en Google
          </a>
          <button type="button" class="google-review__secondary" @click="dismiss">Ahora no</button>
          <p class="google-review__hint">Solo te llevará un minuto.</p>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, watch, ref } from 'vue';
import { dismissGoogleReview, googleReviewState as review, markGoogleReviewClicked } from '@/services/googleReview';

const sheet = ref(null);

const copy = computed(() => {
  const name = review.restaurantName ? review.restaurantName : 'el restaurante';
  const help = `Si tienes un momento, cuéntalo en Google: tu reseña ayuda a ${name} a mejorar y a que más gente lo descubra.`;
  if (review.moment === 'bill-requested') {
    return { title: 'Mientras llega la cuenta…', text: `¿Qué tal ha ido todo? ${help}` };
  }
  if (review.moment === 'order-finished') {
    return { title: '¿Qué tal tu pedido?', text: help };
  }
  return { title: '¡Gracias por tu visita!', text: `¿Qué tal ha ido todo? ${help}` };
});

function dismiss() {
  dismissGoogleReview();
}

watch(() => review.visible, async (visible) => {
  if (!visible) return;
  await nextTick();
  sheet.value?.focus({ preventScroll: true });
});
</script>

<style scoped>
.google-review {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.google-review__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
}

.google-review__sheet {
  position: relative;
  width: min(100%, 440px);
  padding: 28px 22px calc(18px + env(safe-area-inset-bottom));
  border-radius: 26px 26px 0 0;
  background: #fff;
  box-shadow: 0 -12px 40px rgba(15, 23, 42, 0.22);
  text-align: center;
  color: #0a2533;
  outline: none;
}

.google-review__close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  font-size: 1rem;
}

.google-review__stars {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 14px;
  color: #fbbc04;
  font-size: 1.55rem;
}

.google-review__stars i {
  animation: google-review-star 0.45s ease-out both;
}

.google-review__title {
  margin: 0 0 8px;
  font-size: 1.3rem;
  font-weight: 800;
}

.google-review__text {
  margin: 0 auto 20px;
  max-width: 340px;
  color: #475569;
  font-size: 0.95rem;
  line-height: 1.45;
}

.google-review__primary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  border-radius: 16px;
  background: var(--primary-color, #f47700);
  color: #fff;
  font-size: 1.02rem;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 8px 20px rgba(var(--primary-color-rgb, 244, 119, 0), 0.28);
}

.google-review__primary:hover,
.google-review__primary:focus-visible {
  color: #fff;
  filter: brightness(1.05);
}

.google-review__primary:focus-visible,
.google-review__secondary:focus-visible,
.google-review__close:focus-visible {
  outline: 3px solid rgba(var(--primary-color-rgb, 244, 119, 0), 0.45);
  outline-offset: 2px;
}

.google-review__secondary {
  width: 100%;
  min-height: 46px;
  margin-top: 8px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: #64748b;
  font-weight: 700;
}

.google-review__hint {
  margin: 4px 0 0;
  color: #94a3b8;
  font-size: 0.78rem;
}

@media (min-width: 640px) {
  .google-review {
    align-items: center;
  }

  .google-review__sheet {
    padding-bottom: 20px;
    border-radius: 26px;
  }
}

.google-review-enter-active,
.google-review-leave-active {
  transition: opacity 0.22s ease;
}

.google-review-enter-active .google-review__sheet,
.google-review-leave-active .google-review__sheet {
  transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.google-review-enter-from,
.google-review-leave-to {
  opacity: 0;
}

.google-review-enter-from .google-review__sheet,
.google-review-leave-to .google-review__sheet {
  transform: translateY(40px);
}

@keyframes google-review-star {
  from { opacity: 0; transform: scale(0.4); }
  to { opacity: 1; transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .google-review__stars i,
  .google-review-enter-active .google-review__sheet,
  .google-review-leave-active .google-review__sheet {
    animation: none;
    transition: none;
  }
}
</style>
